import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GroupHeader from '../components/group/GroupHeader';
import PresetsGrid from '../components/group/PresetsGrid';
import useBootstrap from '../hooks/useBootstrap';
import { findGroupInBootstrap, usePresetGroupQuery } from '../core/queries/presetGroups';
import { useStore } from '../store';
import PhotoPickSheet from '../components/catalog/PhotoPickSheet';
import { getPhotoCount } from '../utils/photoReferences';
import useConsentGuard from '../hooks/useConsentGuard';

/**
 * PresetGroupDetail — экран одной группы пресетов (`/group/:groupId`).
 *
 * Figma source: `318:4659` template-image (402×809). Layout:
 *   - `GroupHeader` 402×402 — cover + logo overlay + N шаблонов badge
 *   - Body (`px-4 pt-2 pb-8`):
 *       • h2 «Выберите шаблон» — Raleway Bold 16 text-text-soft
 *         (text-soft = rgba(255,255,255,0.9), добавлен в figma-tokens.json
 *          как закрытие stage-03 MAJOR C6; figma 318:4666 opacity=0.9).
 *       • `PresetsGrid` — 2-col × 179×239 preset cards gap 12
 *
 * Data flow:
 *   - `apiClient.generate.getPresetGroups()` — stage 01 endpoint, возвращает
 *     `{ groups: [...] }` (см. msw-handlers `presetGroupsFixture`).
 *   - `useParams().groupId` → `groups.find(g => g.id === groupId)`.
 *   - 404 → «Группа не найдена» (figma-spec §6).
 *
 * UX:
 *   - `scrollTo(0,0)` на mount и при смене `groupId`
 *     (handoff §6 — «рубрика тоже всегда открывается сверху»).
 *   - Нет собственной back-кнопки — нативный Telegram back (handoff §23).
 *   - Tap preset → opens photo pick sheet, matching catalog single-preset UX.
 */
export default function PresetGroupDetail() {
  const { t } = useTranslation();
  const { groupId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: bootstrap, loading: bootstrapLoading } = useBootstrap();
  const generateImage = useStore((state) => state.generateImage);
  const { requireConsent } = useConsentGuard();
  const [activePreset, setActivePreset] = useState(null);
  const [references, setReferences] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sheetError, setSheetError] = useState(null);
  const createInFlightRef = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo(0, 0);
      } catch {
        /* jsdom noop */
      }
    }
  }, [groupId]);

  const bootstrapGroup = useMemo(
    () => findGroupInBootstrap(bootstrap, groupId),
    [bootstrap, groupId]
  );
  const stateGroup = location.state?.group?.id === groupId ? location.state.group : null;
  const groupQuery = usePresetGroupQuery(groupId, {
    initialGroup: stateGroup || bootstrapGroup,
    enabled: !stateGroup && !bootstrapLoading && !bootstrapGroup,
  });
  const group = stateGroup || bootstrapGroup || groupQuery.data || null;
  const loading = !group && (bootstrapLoading || groupQuery.isLoading);

  const presets = group?.presets || [];
  const requiredRefCount = activePreset ? getPhotoCount(activePreset) : 1;

  const closeSheet = useCallback(() => {
    if (generating) return;
    setActivePreset(null);
    setReferences([]);
    setSheetError(null);
  }, [generating]);

  const openSheet = useCallback((preset) => {
    setActivePreset(preset);
    setReferences([]);
    setSheetError(null);
  }, []);

  const performCreate = useCallback(async ({ prompt = '', batchCount = 1 } = {}) => {
    if (createInFlightRef.current) return;
    if (!activePreset) return;
    const readyRefs = references.filter(Boolean);
    if (readyRefs.length < requiredRefCount) return;

    createInFlightRef.current = true;
    setGenerating(true);
    setSheetError(null);
    navigate('/gallery');
    try {
      await generateImage(activePreset.id, prompt, '9:16', '1.5K', batchCount, readyRefs);
    } catch (error) {
      setSheetError(t('prompt.genError', 'Generation error'));
    } finally {
      createInFlightRef.current = false;
      setGenerating(false);
    }
  }, [activePreset, generateImage, navigate, references, requiredRefCount, t]);

  const handleCreate = useCallback(
    (options = {}) => requireConsent(() => performCreate(options), 'generate'),
    [performCreate, requireConsent],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-app flex items-center justify-center text-text-primary">
        {t('common.loading')}
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-bg-app flex items-center justify-center text-text-primary">
        {t('groupDetail.notFound')}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-app text-text-primary">
      {/* Admin grants groups an optional `titleImage` via the Telegram
          admin-bot (managers/group-title-manager.js → presetGroups.json).
          When present it doubles as the brand asset on the home hero AND
          this page — use it as the header overlay; fall back to the
          legacy `logoUrl` so groups without titleImage stay unchanged. */}
      <GroupHeader
        coverUrl={group.cover}
        videoUrl={group.coverVideo || null}
        logoUrl={group.titleImage || group.logoUrl || null}
        logoLayout={group.titleImageLayout || group.titleImageAspect}
        templateCount={presets.length}
      />
      <div className="px-4 pt-2 pb-8 flex flex-col gap-3">
        <h2 className="font-sans font-bold text-h2 text-text-soft">
          {t('groupDetail.chooseTemplate')}
        </h2>
        <PresetsGrid
          presets={presets}
          onTapPreset={openSheet}
        />
      </div>

      <PhotoPickSheet
        preset={activePreset}
        references={references}
        requiredCount={requiredRefCount}
        loading={generating}
        error={sheetError}
        onClose={closeSheet}
        onReferencesChange={setReferences}
        onCreate={handleCreate}
      />
    </div>
  );
}
